import { AttributeKindEnum } from "@brewbase/db/types";
import { SimpleSelect } from "../ui/simple-select";
import {
  Hash,
  Type,
  CheckSquare,
  Clock,
  Calendar,
  Calculator,
  Package,
  Table,
  Paperclip,
  FileText,
} from "lucide-react";

interface KindOption {
  value: AttributeKindEnum;
  label: string;
  description?: string;
  icon?: React.ReactNode;
}

interface AttributeKindSelectorProps {
  value: AttributeKindEnum | undefined;
  onChange: (value: AttributeKindEnum) => void;
  disabled?: boolean;
  className?: string;
  /** Restrict/relabel the offered kinds. Defaults to the full set. */
  options?: KindOption[];
}

export const kindOptions = [
  {
    value: AttributeKindEnum.SINGLE_NUMBER,
    label: "Number",
    description: "A single numeric measurement",
    icon: <Hash className="h-4 w-4" />,
  },
  {
    value: AttributeKindEnum.TEXT,
    label: "Text",
    description: "Text input with various input modes",
    icon: <Type className="h-4 w-4" />,
  },
  {
    value: AttributeKindEnum.CHECK,
    label: "Checkbox",
    description: "True/false or yes/no value",
    icon: <CheckSquare className="h-4 w-4" />,
  },
  {
    value: AttributeKindEnum.TIMESTAMP,
    label: "Time",
    description: "Time value with date and time",
    icon: <Clock className="h-4 w-4" />,
  },
  {
    value: AttributeKindEnum.DATESTAMP,
    label: "Date",
    description: "Date value without time",
    icon: <Calendar className="h-4 w-4" />,
  },
  {
    value: AttributeKindEnum.COMPUTED_NUMBER,
    label: "Formula",
    description: "Calculated value from a formula",
    icon: <Calculator className="h-4 w-4" />,
  },
  {
    value: AttributeKindEnum.INVENTORY_ITEM_REFERENCE,
    label: "Inventory Item",
    description: "Reference to an inventory item",
    icon: <Package className="h-4 w-4" />,
  },
  {
    value: AttributeKindEnum.TABULAR_DATA,
    label: "Tabular Data",
    description: "Structured table of data",
    icon: <Table className="h-4 w-4" />,
  },
  {
    value: AttributeKindEnum.MEDIA_ATTACHMENT,
    label: "Media Attachment",
    description: "File or media upload",
    icon: <Paperclip className="h-4 w-4" />,
  },
  {
    value: AttributeKindEnum.INSTRUCTION,
    label: "Instruction",
    description: "Rich text instructions for operators",
    icon: <FileText className="h-4 w-4" />,
  },
];

export function AttributeKindSelector({
  value,
  onChange,
  disabled = false,
  className,
  options = kindOptions,
}: AttributeKindSelectorProps) {
  return (
    <SimpleSelect
      options={options}
      value={value?.toString() ?? null}
      onChange={(val) => onChange(val as AttributeKindEnum)}
      disabled={disabled}
    />
  );
}
